import { ArrowPathIcon } from "@heroicons/react/24/outline";
import Button from "src/components/Shared/Buttons/Button";
import Section from "src/components/Shared/Containers/Section";
import Badge from "src/components/ServerMonitoring/Badge";
import { databaseStatusStyles } from "src/components/ServerMonitoring/DatabaseHealth";
import { classNames } from "src/helpers/classNames";
import { formatDateTime, formatPercent, formatRelative, serverStatusStyles } from "src/helpers/serverMonitorHelper";

const deploymentPhaseStyles = {
  ACTIVE: "bg-green-100 text-green-700",
  ERROR: "bg-red-100 text-red-700",
  CANCELED: "bg-gray-100 text-gray-600",
  SUPERSEDED: "bg-gray-100 text-gray-600",
};

const KIND_LABELS = { service: "Service", worker: "Worker", job: "Job", function: "Functions", static_site: "Static site", database: "Database" };

// Components DigitalOcean does not health-check: explain why instead of showing "Unknown".
const noHealthBadge = (component) => {
  if (component.kind === "static_site") return { label: "Served by CDN", className: "bg-sky-50 text-sky-700", note: "Static files - no server to crash" };
  if (component.kind === "database") return { label: "Attached", className: "bg-gray-100 text-gray-600", note: "Managed database - health not reported to the app" };
  return { label: "No health data", className: "bg-gray-100 text-gray-600", note: null };
};

const phaseLabel = (phase) => (phase ? phase.charAt(0) + phase.slice(1).toLowerCase().replace(/_/g, " ") : "Unknown");

const InfoItem = ({ label, children }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <div className="text-sm font-semibold text-gray-900">{children}</div>
  </div>
);

// Overall status of the whole DigitalOcean App Platform app: every component and its deployments.
const AppStatusCard = ({ app, monitoredComponent, monitoredStatus, databases = [], databaseStats = null, loaded, checking, configured, onCheckNow }) => {
  // DigitalOcean's own health lags behind (and a process that dies inside a running container can look HEALTHY for
  // minutes). When the Super Portal has detected that the Node server crashed, the app status reflects that too.
  const nodeCrashed = monitoredStatus === "crashed";
  const monitoredHealthy = app?.components?.some((component) => component.name === monitoredComponent && component.health_reported !== false && component.state === "HEALTHY");
  const healthyCount = Math.max(0, (app?.components_healthy || 0) - (nodeCrashed && monitoredHealthy ? 1 : 0));
  const totalCount = app?.components_total || 0;
  const appStatus = nodeCrashed ? (healthyCount === 0 ? "unhealthy" : "degraded") : app?.status;
  const style = serverStatusStyles[appStatus] || serverStatusStyles.unknown;
  const active = app?.active_deployment;
  const inProgress = app?.in_progress_deployment;

  return (
    <Section shadow>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">DigitalOcean App</h2>
          {app?.name && <span className="text-sm text-gray-500">{app.name}</span>}
          {loaded && (
            <Badge
              label={style.label}
              className={style.badge}
              dot={style.dot}
            />
          )}
          {nodeCrashed && app?.status === "online" && <span className="text-xs text-gray-400">DigitalOcean still reports healthy - its health check has not caught up yet</span>}
          {inProgress && (
            <Badge
              label={`Deploying: ${phaseLabel(inProgress.phase)}${inProgress.progress ? ` (${inProgress.progress.success_steps}/${inProgress.progress.total_steps})` : ""}`}
              className="bg-blue-100 text-blue-700"
            />
          )}
        </div>
        <Button
          version="secondary"
          type="button"
          onClick={onCheckNow}
          disabled={checking || !configured}
          loading={checking}>
          <ArrowPathIcon className="h-4 w-4" />
          Check now
        </Button>
      </div>

      {loaded && !app ? (
        <p className="mt-4 text-sm text-gray-400">No app details yet. They appear after the first successful check.</p>
      ) : (
        app && (
          <>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <InfoItem label="Services Healthy">
                <span className={classNames(healthyCount < totalCount && "text-red-600")}>
                  {healthyCount} / {totalCount}
                </span>
                {app.components?.length > app.components_total && <div className="text-xs font-normal text-gray-400">{app.components.length} components in total</div>}
              </InfoItem>
              <InfoItem label="Current Deployment">
                {active ? (
                  <div className="flex flex-col gap-1">
                    {/* "ACTIVE" is DigitalOcean's name for "this deployment went live" - not "the API is working". */}
                    <Badge
                      label={active.phase === "ACTIVE" ? "Live" : phaseLabel(active.phase)}
                      className={classNames("self-start", deploymentPhaseStyles[active.phase] || "bg-gray-100 text-gray-600")}
                    />
                    <span className="text-xs font-normal text-gray-400">{formatDateTime(active.updated_at || active.created_at, "MMM D, YYYY h:mm A")}</span>
                  </div>
                ) : (
                  "None"
                )}
              </InfoItem>
              <InfoItem label="Last Checked">
                {formatRelative(app.checked_at)}
                {app.region && <div className="text-xs font-normal text-gray-400">Region: {app.region}</div>}
              </InfoItem>
            </div>

            {app.messages?.length > 0 && app.status !== "online" && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {app.messages.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}
            {active?.cause && (
              <p className="mt-3 truncate text-xs text-gray-400">
                Last deployment
                {(active.updated_at || active.created_at) && (
                  <>
                    {" "}
                    ({formatDateTime(active.updated_at || active.created_at, "MMM D, YYYY h:mm A")}, {formatRelative(active.updated_at || active.created_at)})
                  </>
                )}
                : {active.cause}
              </p>
            )}

            {app.components?.length > 0 && (
              <div className="mt-5 overflow-x-auto rounded-md border border-gray-100">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                    <tr>
                      <th className="px-4 py-2 font-medium">Component</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">CPU</th>
                      <th className="px-4 py-2 font-medium">Memory</th>
                      <th className="px-4 py-2 font-medium">Replicas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {app.components.map((component) => {
                      const crashed = component.name === monitoredComponent && monitoredStatus === "crashed";
                      const reported = component.health_reported !== false;
                      const componentStyle = serverStatusStyles[crashed ? "crashed" : component.status] || serverStatusStyles.unknown;
                      const databaseInfo = component.kind === "database" ? databases.find((database) => database.name === component.name) : null;
                      const databaseStyle = databaseInfo ? databaseStatusStyles[databaseInfo.status] || databaseStatusStyles.unknown : null;
                      const noHealth = reported ? null : noHealthBadge(component);
                      return (
                        <tr key={component.name}>
                          <td className="px-4 py-2 font-medium text-gray-900">
                            {component.name}
                            {component.name === monitoredComponent && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500">Node server - crash monitored</span>}
                            <div className="text-xs font-normal text-gray-400">
                              {KIND_LABELS[component.kind] || "Component"}
                              {component.engine ? ` (${component.engine})` : ""}
                              {component.routes?.length > 0 && <> · {component.routes.join(", ")}</>}
                            </div>
                            {component.state !== "HEALTHY" && component.messages?.length > 0 && <div className="text-xs font-normal text-amber-700">{component.messages.join(" ")}</div>}
                          </td>
                          <td className="px-4 py-2">
                            {databaseStyle ? (
                              <Badge
                                label={databaseStyle.label}
                                className={databaseStyle.badge}
                                dot={databaseStyle.dot}
                              />
                            ) : reported ? (
                              <Badge
                                label={componentStyle.label}
                                className={componentStyle.badge}
                                dot={componentStyle.dot}
                              />
                            ) : (
                              <>
                                <Badge
                                  label={noHealth.label}
                                  className={noHealth.className}
                                />
                                {noHealth.note && <div className="mt-1 text-xs text-gray-400">{noHealth.note}</div>}
                              </>
                            )}
                          </td>
                          {(() => {
                            // The database row uses MongoDB's own figures (same as the Database section below).
                            const stats = databaseInfo?.stats_available && databaseStats && !databaseStats.error ? databaseStats : null;
                            const cpu = stats ? stats.cpu_percent : component.cpu;
                            const memory = stats ? stats.memory_percent : component.memory;
                            const replicas =
                              component.kind === "database"
                                ? databaseInfo?.num_nodes != null
                                  ? `${databaseInfo.num_nodes} node${databaseInfo.num_nodes === 1 ? "" : "s"}`
                                  : "—"
                                : component.replicas_ready != null || component.replicas_desired != null
                                  ? `${component.replicas_ready ?? "—"} / ${component.replicas_desired ?? "—"}`
                                  : "—";
                            return (
                              <>
                                <td className="px-4 py-2 text-gray-600">{formatPercent(cpu)}</td>
                                <td className="px-4 py-2 text-gray-600">{formatPercent(memory)}</td>
                                <td className="px-4 py-2 text-gray-600">{replicas}</td>
                              </>
                            );
                          })()}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}
    </Section>
  );
};

export default AppStatusCard;
